import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { Box, Flex } from '@/layout';
import { Checkbox, FormControlLabel, Modal } from '@mui/material';
import DataTable, { FileMenu, TypeColumn } from '@/components/DataTable';

import { ModalActions, ModalTitle } from '../style';
import { successHandler, errorHandler } from '@/utils/toastHandler';
import { Close, Search } from '@/components/Icon';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { TextField } from '@mui/material';
import { getShareUsers, createShare } from '@/api/document.api';
import { SearchBar, ModalForm, FeatureButton, IconWrapper } from './style';
import type { ColumnDef } from '@tanstack/react-table';
import Loading from '@/components/UI/Loading';
import { Text } from '@/components/UI/Typography/Text';
import { buildShareUserSearchParams, mapShareUsers } from './shareUserSearch';

interface ShareMenuProps {
  targetId: number | null;
  type: string;
  show: boolean;
  setShowShareMenuModal: (show: boolean) => void;
}

interface DataTableColumns {
  id: string;
  name: string;
  company: string;
  email: string;
}

interface createShareRequired {
  target_type: 'folder' | 'document';
  target_id: number;
  recipient_user_ids: number[];
}

const ShareMenuModal = ({ type, targetId, show, setShowShareMenuModal }: ShareMenuProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [memberList, setMemberList] = useState<DataTableColumns[]>([]);
  const [queryStrings, setQueryStrings] = useState<string>('');
  const [searchRevision, setSearchRevision] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const loadRequestIdRef = useRef(0);
  const sharePending = useRef(false);
  const [isSharing, setIsSharing] = useState(false);

  const {
    control,
    resetField,
    handleSubmit,
    getValues
  } = useForm<{ queryStrings: string }>();

  const onSubmit = handleSubmit(async (data) => {
    if (!targetId || selectedIds.length === 0 || sharePending.current) return;
    sharePending.current = true;
    setIsSharing(true);
    try {
      const rawData: createShareRequired = {
        target_type: type === 'folder' ? 'folder' : 'document',
        target_id: targetId,
        recipient_user_ids: selectedIds.map(id => Number(id)),
      }
      const res = (await createShare(rawData)).data;

      if (res.success) {
        successHandler(type + ' successfully shared');
        setShowShareMenuModal(false);
        resetField('queryStrings');
        setQueryStrings('')
        setMemberList([]);
        setPage(1);
        setSelectedIds([]);
      }
    } catch (error: any) {
      if (error.response?.data?.data?.code === 'SHARE_RECIPIENT_NOT_ELIGIBLE') {
        setSelectedIds([]);
        setSearchRevision((current) => current + 1);
        errorHandler(t('pages.docLibrary.modal.shareToUser.recipientUnavailable'));
      } else {
        errorHandler(t('error.server'));
      }
    } finally {
      sharePending.current = false;
      setIsSharing(false);
    }
  });

  const searchCompany = () => {
    const data = getValues();
    setPage(1);
    setQueryStrings((data.queryStrings || '').trim());
    setSearchRevision((current) => current + 1);
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedIds((current) => Array.from(new Set([...current, ...memberList.map((item) => item.id)])));
  };

  const handleClearSelect = () => {
    setSelectedIds([]);
  };

  const columns: ColumnDef<DataTableColumns>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: '☐',
        cell: (info) => (
          <FormControlLabel
            key={info.row.original.id}
            control={
              <Checkbox
                sx={{
                  '&.Mui-checked': {
                    color: '#7EB442',
                  },
                }}
                onChange={() => handleCheckboxChange(info.row.original.id)}
                checked={selectedIds.includes(info.row.original.id)}
                inputProps={{ 'aria-label': `${info.row.original.name} (${info.row.original.email})` }}
              />
            }
            label={<label htmlFor=""></label>}
          />
        ),
      },
      {
        accessorKey: 'name',
        header: t('pages.docLibrary.modal.shareToUser.name'),
        sortable: true,
      },
      {
        accessorKey: 'email',
        header: t('pages.docLibrary.modal.shareToUser.email'),
        sortable: true,
      },
      {
        accessorKey: 'company',
        header: t('pages.docLibrary.modal.shareToUser.company'),
      },
    ],
    [t, selectedIds]
  );

  const initData = useCallback(async (queryStrings: string) => {
    const requestId = ++loadRequestIdRef.current;
    setIsLoading(true);
    setLoadError(false);
    setMemberList([]);
    try {
      const res = (await getShareUsers(buildShareUserSearchParams(page, perPage, queryStrings))).data;
      if (requestId !== loadRequestIdRef.current) return;
      if (res.success) {
        setMemberList(mapShareUsers(res.data));

        setTotal(res.meta.total);
      } else {
        setLoadError(true);
      }
    } catch (error: any) {
      if (requestId === loadRequestIdRef.current) setLoadError(true);
    } finally {
      if (requestId === loadRequestIdRef.current) setIsLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    if (show) {
      void initData(queryStrings);
    }

    return () => {
      loadRequestIdRef.current += 1;
    };
  }, [show, queryStrings, searchRevision, initData]);

  useEffect(() => {
    if (show) setSelectedIds([]);
  }, [show, targetId, type]);

  return (
    <Modal
      open={show}
      onClose={() => {
        setShowShareMenuModal(false);
        resetField('queryStrings');
        setQueryStrings('')
        setMemberList([]);
        setPage(1);
      }}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      disableEnforceFocus
    >
      <ModalForm onSubmit={onSubmit}>
        <Close.ButtonClose
          type="button"
          onClick={() => {
            setShowShareMenuModal(false);
            resetField('queryStrings');
            setQueryStrings('')
            setMemberList([]);
            setPage(1);
          }}
        >
          <Close.CloseSVG />
        </Close.ButtonClose>
        <ModalTitle>{t('pages.docLibrary.modal.shareToUser.title')}</ModalTitle>
        <Text>{t('pages.docLibrary.modal.shareToUser.directoryOnly')}</Text>
        <Flex flexDirection={'column'} gridGap={'20px'}>
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <SearchBar>
              <Controller
                control={control}
                defaultValue=""
                name={'queryStrings'}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div style={{ width: '100%' }}>
                    <TextField
                      placeholder={t('pages.docLibrary.modal.shareToUser.searchPlaceholder')}
                      variant="outlined"
                      value={value}
                      sx={{ width: '100%', background: '#FFF', borderRadius: '5px 0 0 5px' }}
                      onChange={onChange}
                      size="small"
                      error={!!error}
                      helperText={error ? error.message : null}
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // 阻止表單預設送出
                          searchCompany(); // 手動觸發搜尋
                        }
                      }}
                    />
                    <IconWrapper as="button" type="button" onClick={searchCompany} aria-label={t('pages.docLibrary.modal.shareToUser.search')}>
                      <img width={30} src={Search.SearchSVG_B} alt="searchSVG" />
                    </IconWrapper>
                  </div>
                )}
              />
            </SearchBar>
          </Flex>
          <Flex gridGap={'20px'}>
            <FeatureButton type="button" onClick={handleSelectAll}>{t('pages.docLibrary.modal.shareToUser.selectAll')}</FeatureButton>
            <FeatureButton type="button" onClick={handleClearSelect}>{t('pages.docLibrary.modal.shareToUser.clearSelect')}</FeatureButton>
          </Flex>
          <Text role="status">{t('pages.docLibrary.modal.shareToUser.selectedCount', { count: selectedIds.length })}</Text>
          {isLoading ? <Loading /> : loadError ? (
            <Flex alignItems="center" gridGap="12px">
              <Text>{t('table.status.error')}</Text>
              <FeatureButton type="button" onClick={() => setSearchRevision((current) => current + 1)}>
                {t('table.actions.retry')}
              </FeatureButton>
            </Flex>
          ) : memberList.length === 0 ? (
            <Text>{t('table.status.empty')}</Text>
          ) : (
            <DataTable setPage={setPage} setPerPage={setPerPage} page={page} perPage={perPage} total={total} columns={columns} data={memberList} />
          )}
        </Flex>
        <ModalActions>
          <Button type="submit" disabled={isLoading || isSharing || selectedIds.length === 0} size={'lg'} variant={'primary'} rounded={false}>
            {t('buttons.ok')}
          </Button>
          <Button
            type="button"
            size={'lg'}
            variant={'primary-invert'}
            color="primary"
            onClick={() => setShowShareMenuModal(false)}
            style={{ textDecoration: 'underline' }}
          >
            {t('buttons.cancel')}
          </Button>
        </ModalActions>
      </ModalForm>
    </Modal >
  );
};

export default ShareMenuModal;
